import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { ConnectComponentsProvider, ConnectNotificationBanner } from '@stripe/react-connect-js';
import connectApi from '../../../services/connectApi';
import useAccountSession from '../../connect/use-account-session';
import useConnectInstance from '../../connect/use-connect-instance';
import FinanceRouteBoundary from '../finance-route-boundary';
import FinanceSkeleton from '../components/finance-skeleton';
import { FinanceSectionContext } from './finance-section-context';
import { bannerFrame } from '../pages/finance-page-shell';
import { setConnectStatus } from '../../../store/slices/companySlice';
import { isConnectPending, isLegacyOnboarding } from '../../../utils/connectModel';

export const CONNECT_DIRECT = 'CONNECT_DIRECT';
export const LEGACY = 'LEGACY';

// Pages that only exist on direct charges. A legacy company deep-linking to
// one lands on its own Overview rather than an empty page it can never use.
const CONNECT_ONLY = ['payouts', 'order-reports', 'refunds', 'disputes'];

// The Connect finance page used to carry its sections as ?tab=; those links
// are in bookmarks and in the QA spec, and they now name real pages. Only the
// restaurant's own route translates them — the admin's Settlement tab keeps
// its own ?tab= contract (spec 2026-09-14 §8).
const TAB_TO_PAGE = {
  summary: '',
  payments: 'order-reports',
  refunds: 'refunds',
  payouts: 'payouts',
  disputes: 'disputes',
};

// Kept only to key useConnectInstance's effect against the other surfaces
// (admin), the way account-sections.js's SETTINGS_COMPONENTS did — never sent
// to Stripe or to our server, which decides what a restaurant session carries.
const SECTION_COMPONENTS = [
  'account_onboarding',
  'account_management',
  'documents',
  'notification_banner',
  'payouts',
  'payouts_list',
  'payout_details',
  'disputes_list',
];

// The whole /dashboard/finance section: it resolves the company's payment
// model once, mints one Stripe Account Session for a company that has an
// account, and hands both down through context. Every sub-page is a route
// under this one, so moving between them never re-mints the session.
//
// This replaces FinanceRouteGate, which chose between two whole screens. The
// choice is the same; what changed is that the answer now shapes a section
// rather than picking one component.
//
// Split into FinanceSectionInner + this wrapper for the same reason
// finance-route-boundary.js's own comment gives: "a component cannot catch
// an error thrown by its own render". FinanceSectionInner calls
// useAccountSession/useConnectInstance directly in its own body, so a
// boundary it returned itself would never get built if either hook threw —
// the throw happens before that return statement runs. The boundary has to
// already exist as an ancestor, so it lives out here instead.
const FinanceSection = () => (
  <FinanceRouteBoundary>
    <FinanceSectionInner />
  </FinanceRouteBoundary>
);

const FinanceSectionInner = () => {
  const roles = useSelector((state) => state.authReducer.roles);
  const company = useSelector((state) => state.companyReducer.selected);
  const isDark = useSelector((state) => state.commonReducer.isDark);
  const isAdmin = !!roles?.includes(100);
  // Who may touch the bank. Payouts and its Stripe components are gated to
  // [150, 200] in router.js; Overview is also open to the accountant (550),
  // who reads the money and must be offered no way to a page the router
  // refuses. Computed once here so every Overview card asks the same
  // question.
  const canManagePayouts = (roles || []).some((r) => r === 150 || r === 200);
  // The company's own payout_paused setting already drives layout.js's
  // section-wide red banner; Overview's health line reads the same fact so
  // it never says "on schedule" under it.
  const payoutsPaused = useSelector(
    (state) => state.companyReducer.settings?.payout_paused?.value === 'true',
  );
  const dispatch = useDispatch();
  const companyId = company?.id;
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // `companyId` on the answer ties it to the company it was read for, so a
  // company switch shows the skeleton again instead of the old answer.
  const [resolved, setResolved] = useState({ companyId: null, status: null });

  // One read, callable from two places: the mount/company-switch effect
  // below, and `refreshStatus` in the context value further down. Kept as a
  // stable function with no dependencies — the id to read for is always
  // passed in, never closed over — so neither caller's own effect/callback
  // has to depend on anything that changes more often than it needs to.
  const readStatus = useCallback(async (id) => {
    try {
      return connectApi.toConnectStatus(await connectApi.getStatus(id));
    } catch {
      // Fail-closed: an unreadable status is legacy, which is what this
      // company saw yesterday. Never a promotion to Connect on a blip.
      return null;
    }
  }, []);

  // Guards every write `readStatus`'s two callers issue, the same way the
  // old inline `active` flag guarded the mount effect's own single read —
  // except this counter is shared across both callers, so a manual
  // `refreshStatus()` call and a company switch that lands mid-flight can
  // never let the older of the two overwrite the newer.
  const requestId = useRef(0);

  useEffect(() => {
    if (isAdmin || !companyId) return undefined;
    requestId.current += 1;
    const id = requestId.current;
    setResolved({ companyId: null, status: null });
    (async () => {
      const status = await readStatus(companyId);
      if (id === requestId.current) {
        setResolved({ companyId, status });
        // Same guard, same fact, as refreshStatus below: the sidebar and the
        // Pending band read the store's copy (spec 2026-09-25 §5.6), which
        // otherwise only ever gets written by layout.js's own mount/switch
        // effect or by refreshStatus itself — neither of which reruns after
        // an admin's Activate on a different tab/session. Writing it here
        // too means the next time this restaurant visits Finance (or
        // reloads), the sidebar and band catch up without a full reload.
        // null (a failed read) never overwrites the last good answer.
        if (status) dispatch(setConnectStatus({ companyId, status }));
      }
    })();
    return undefined;
  }, [companyId, isAdmin, readStatus, dispatch]);

  // The Account page calls this once onboarding finishes in place
  // (ConnectOnboarding's own `onStatusChange`): this section resolved
  // `hasAccount` once, on mount, and a page under it has no other way to
  // tell it that answer is now stale. Re-running the same read the mount
  // effect uses is what lets the rest of this component's own state follow:
  // a newly-true `hasAccount` changes `secretSource` below from `null` to
  // `fetchSecret` (a stable reference from useAccountSession), which is
  // exactly the dependency useConnectInstance's own effect re-runs on — see
  // that hook's `[secretSource, componentKey]` dependency array — so the
  // Stripe session gets minted without this section, or the page under it,
  // ever remounting.
  const refreshStatus = useCallback(async () => {
    if (!companyId) return;
    requestId.current += 1;
    const id = requestId.current;
    const targetCompanyId = companyId;
    const status = await readStatus(targetCompanyId);
    if (id !== requestId.current) return;
    setResolved({ companyId: targetCompanyId, status });
    // The sidebar and the Pending band read the store's copy (spec
    // 2026-09-25 §5.6). A failed read leaves that copy as it was: null is
    // "could not read", never a new answer.
    if (status) dispatch(setConnectStatus({ companyId: targetCompanyId, status }));
  }, [companyId, readStatus, dispatch]);

  const status = resolved.status;
  const hasAccount = !!status?.hasAccount;
  const model = status?.paymentModel === CONNECT_DIRECT ? CONNECT_DIRECT : LEGACY;

  // Minted whenever there is a Stripe account, not only on CONNECT_DIRECT:
  // The Account page has to work during onboarding, before the admin flips
  // the model (spec 2026-09-14 §2).
  const { fetchSecret, error: sessionError } = useAccountSession(
    hasAccount ? companyId : null,
    'finance',
  );
  // Stripe's session can fail for reasons that pass — a blip on our own
  // AccountSession endpoint, a secret that expired while a modal sat open.
  // There is no re-mint function in either hook (useAccountSession's
  // fetchSecret is a stable useCallback; useConnectInstance's build effect
  // keys on [secretSource, componentKey]), so the one honest lever is the
  // component key: the list is never sent to Stripe or to our server — see
  // use-connect-instance.js's own comment — so a nonce in it only re-runs
  // the build, which calls fetchClientSecret again and clears
  // useAccountSession's error on success.
  const [sessionAttempt, setSessionAttempt] = useState(0);
  const retrySession = useCallback(() => setSessionAttempt((n) => n + 1), []);
  const sessionComponents = useMemo(
    () => [...SECTION_COMPONENTS, `retry-${sessionAttempt}`],
    [sessionAttempt],
  );

  const { instance, error: instanceError } = useConnectInstance(
    hasAccount ? fetchSecret : null,
    sessionComponents,
    isDark,
  );

  // Precedence, the same one account-sections.js settled on: a session that
  // failed wins over an `instance` that merely exists. useConnectInstance
  // builds the instance synchronously, so it is truthy the moment a
  // publishable key is configured, long before Stripe ever calls
  // fetchClientSecret and discovers the secret is refused.
  const sessionFailure = sessionError || instanceError?.message || '';
  // `hasAccount` gates this explicitly, not only through the hooks' own
  // inputs: in production useConnectInstance already returns a null instance
  // when handed a null secretSource (hasAccount false), but a test double for
  // that hook has no reason to replicate that branching, so this component's
  // own contract — no Stripe provider for a company with no account — has to
  // hold regardless of what the hook returns.
  const sessionOk = hasAccount && !!instance && !sessionFailure;

  // Finance Overview v3 (2026-10-11): "Your First Stripe Payout". Read once
  // per company here, so Overview, Payouts and a payout's page agree, and
  // closing it on one closes it on all three at once. A refused read (an
  // accountant without connect-manage) or a failed one is simply no banner.
  //
  // Only once the status is the one read for THIS company: on a switch,
  // `model` still holds the previous company's answer for a render, and the
  // read must not go out on it.
  const statusIsCurrent = resolved.companyId === companyId;
  const [notice, setNotice] = useState({ companyId: null, kind: null });
  useEffect(() => {
    if (isAdmin || !companyId || !statusIsCurrent || model !== CONNECT_DIRECT) return undefined;
    let active = true;
    (async () => {
      try {
        const kind = connectApi.toFirstPayoutNotice(await connectApi.getFirstPayoutNotice(companyId));
        if (active) setNotice({ companyId, kind });
      } catch {
        // No banner.
      }
    })();
    return () => {
      active = false;
    };
  }, [companyId, isAdmin, statusIsCurrent, model]);

  // Closed at once. The API keeps it closed on every device and after a
  // refresh; a refused write leaves it closed for this visit only.
  const dismissFirstPayoutNotice = useCallback(() => {
    if (!companyId) return;
    setNotice({ companyId, kind: null });
    connectApi.dismissFirstPayoutNotice(companyId).catch(() => {});
  }, [companyId]);

  const resolving = !isAdmin && (!companyId || resolved.companyId !== companyId);

  const value = {
    model: isAdmin ? LEGACY : model,
    isAdmin,
    companyId,
    status,
    statusLoaded: isAdmin || resolved.companyId === companyId,
    instance: sessionOk ? instance : null,
    sessionFailure,
    refreshStatus,
    canManagePayouts,
    payoutsPaused,
    retrySession,
    firstPayoutNotice: notice.companyId === companyId ? notice.kind : null,
    dismissFirstPayoutNotice,
  };

  if (resolving) return <FinanceSkeleton />;

  // Never for the admin: the pages `legacyTab` would redirect to (Payouts,
  // Order Reports, Refunds, Disputes) are gated to roles [150, 200, 550] in
  // router.js — an admin has none of those — so translating this old link
  // for role 100 would bounce them straight to '/dashboard' instead of
  // showing anything. The admin's own ?tab= contract lives on the
  // Settlement tab (connect-finance.js) — and now also on the Finance
  // console's own tabs (admin/admin-finance.js) — and is untouched by this.
  const legacyTab = TAB_TO_PAGE[searchParams.get('tab')];
  if (!isAdmin && legacyTab !== undefined && location.pathname.replace(/\/$/, '').endsWith('/finance')) {
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    const suffix = next.toString() ? `?${next}` : '';
    return <Navigate to={`/dashboard/finance${legacyTab ? `/${legacyTab}` : ''}${suffix}`} replace />;
  }

  const segment = location.pathname.split('/dashboard/finance/')[1]?.split('/')[0] || '';
  if (value.model !== CONNECT_DIRECT && CONNECT_ONLY.includes(segment)) {
    return <Navigate to="/dashboard/finance" replace />;
  }

  // Owner, 2026-09-25: while a company born Pending is on its way to Connect
  // its Finance section is Business Details and nothing else, deep links
  // included. Activate brings the rest back.
  if (!isAdmin && isConnectPending(status) && segment !== 'business-details') {
    return <Navigate to="/dashboard/finance/business-details" replace />;
  }

  // Owner, 2026-10-03: a legacy company in onboarding is still paid on the
  // legacy path until Activate, so it keeps its legacy Finance and gains
  // Business Details. The direct-charges pages stay closed to it: Tax
  // Documents here (the rest are CONNECT_ONLY above).
  if (!isAdmin && isLegacyOnboarding(status) && segment === 'tax-documents') {
    return <Navigate to="/dashboard/finance/business-details" replace />;
  }

  const frame = bannerFrame(segment, {
    legacyModel: value.model !== CONNECT_DIRECT,
    orderList: location.pathname.replace(/\/$/, '').endsWith('/orders'),
  });

  // The section paints the page ground once, around whatever the route
  // renders. Each page brings its own gutter and column (FinancePageShell); a
  // page that falls back to a legacy screen renders that screen bare, because
  // those components still carry their own full shell — identical background,
  // so the nesting is invisible.
  const shell = (children) => (
    <div className="min-h-[calc(100vh-4rem)] bg-fin-canvas">
      {children}
    </div>
  );

  return (
    <FinanceSectionContext.Provider value={value}>
      {sessionOk ? (
        <ConnectComponentsProvider connectInstance={instance}>
          {shell(
            <>
              {/* Stripe's own banner is section-wide, so it takes the gutter
                  here rather than inside any one page. Same gutter and same
                  column as the page under it has (bannerFrame), or it would
                  sit on a different left edge from every card there. Only top
                  padding: the page below brings its own. */}
              <div className={frame.gutter}>
                <div className={frame.column || undefined}>
                  <ConnectNotificationBanner />
                </div>
              </div>
              <Outlet />
            </>,
          )}
        </ConnectComponentsProvider>
      ) : (
        shell(<Outlet />)
      )}
    </FinanceSectionContext.Provider>
  );
};

export default FinanceSection;
